import { Locale, Timestamp } from 'src/types'
import { TIME_IN_MS } from 'src/constants'

import { ChartConfig } from 'src/components/shadcn/chart'
import { ChartDataItem, MapHistory } from 'src/components/HomeCharBar/types'

export function getChartConfig(
  locale: Locale['homePage']['barChart'],
  colorFuel: string,
  colorMaintenance: string,
): ChartConfig {
  return {
    views: {
      label: locale.spends,
    },
    fuel: {
      label: locale.spendTitleFuel,
      color: colorFuel,
    },
    maintenance: {
      label: locale.spendTitleMaintenance,
      color: colorMaintenance,
    },
  } satisfies ChartConfig
}

// eslint-disable-next-line
export const mapCostHistory = <T extends Record<string, any>>(
  costKey: keyof T,
  dateKey: keyof T,
  currencyKey: keyof T,
  data?: T[],
): MapHistory[] =>
  data?.map(item => {
    return {
      date: item[dateKey],
      countOfCost: item[costKey],
      currency: item[currencyKey],
    }
  }) || []

function normalizeDate(timestamp: Timestamp): string {
  return new Date(timestamp.seconds * TIME_IN_MS.SECOND)
    .toISOString()
    .split('T')[0]
}

function convertToUserCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  convertCurrency: (value: number, from: string, to: string) => number,
): number {
  return fromCurrency === toCurrency
    ? amount
    : convertCurrency(amount, fromCurrency, toCurrency)
}

function processHistory(
  history: MapHistory[],
  type: 'fuel' | 'maintenance',
  aggregatedData: Record<string, { maintenance: number; fuel: number }>,
  currentUserCurrency: string,
  convertCurrency: (value: number, from: string, to: string) => number,
): void {
  history.forEach(({ date, countOfCost, currency }) => {
    const formattedDate = normalizeDate(date)
    const convertedCost = convertToUserCurrency(
      countOfCost,
      currency,
      currentUserCurrency,
      convertCurrency,
    )

    if (!aggregatedData[formattedDate]) {
      aggregatedData[formattedDate] = { maintenance: 0, fuel: 0 }
    }

    aggregatedData[formattedDate][type] += convertedCost
  })
}

export function formatNumberCost(value: number): number {
  return value % 1 === 0 ? value : Number(value.toFixed(2))
}

export function getMappedData(
  fuelHistory: MapHistory[],
  maintenanceHistory: MapHistory[],
  currentUserCurrency: string,
  convertCurrency: (value: number, from: string, to: string) => number,
): ChartDataItem[] {
  const aggregatedData: Record<string, { maintenance: number; fuel: number }> =
    {}

  processHistory(
    fuelHistory,
    'fuel',
    aggregatedData,
    currentUserCurrency,
    convertCurrency,
  )
  processHistory(
    maintenanceHistory,
    'maintenance',
    aggregatedData,
    currentUserCurrency,
    convertCurrency,
  )

  return Object.entries(aggregatedData)
    .map(([date, { maintenance, fuel }]) => ({
      date,
      maintenance: formatNumberCost(maintenance),
      fuel: formatNumberCost(fuel),
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
}
